import { useEffect, useRef, useState } from 'react'
import { formatCurrency } from './format'

interface Props {
  value: number
  onSave: (newValue: number) => void
  /** Disable editing (e.g. while a mutation is in flight or for a
   *  computed/read-only row). */
  disabled?: boolean
  ariaLabel: string
}

/**
 * Click → input field → Enter to save. Esc cancels. Tab also saves
 * and advances naturally (the parent grid manages focus order).
 *
 * Parsing is lenient: strips `$` and `,` so "$1,200.50" works. Empty
 * input clears to 0.
 */
export function AssignCell({ value, onSave, disabled, ariaLabel }: Props) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.select()
  }, [editing])

  function start() {
    if (disabled) return
    setDraft(value === 0 ? '' : value.toFixed(2))
    setEditing(true)
  }

  function commit() {
    const cleaned = draft.replace(/[$,]/g, '').trim()
    const parsed = cleaned === '' ? 0 : Number(cleaned)
    if (Number.isFinite(parsed) && parsed !== value) {
      onSave(Math.max(0, parsed))
    }
    setEditing(false)
  }

  function cancel() {
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
          else if (e.key === 'Escape') cancel()
        }}
        aria-label={ariaLabel}
        className="w-full rounded border border-blue-500 bg-white px-2 py-1 text-right text-sm tabular-nums dark:bg-zinc-900"
      />
    )
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      aria-label={ariaLabel}
      className="w-full rounded px-2 py-1 text-right text-sm tabular-nums hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-zinc-800"
    >
      {formatCurrency(value)}
    </button>
  )
}
